import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  DescriptionOutlined,
  Edit,
  InfoOutlined,
  OpenInNew,
  Visibility,
} from "@mui/icons-material";
import { API_CONFIG, apiService } from "../../../../config/api";
import ManualConsentBuilder, {
  createDefaultManualState,
  manualSectionsToBackendSections,
  manualStateFromFormContent,
  normalizeManualState,
  validateManualConsentState,
  type ManualConsentState,
} from "./ManualConsentBuilder";

type CreateMode = "template" | "manual";

type DataSharingPlan = "none" | "deidentified" | "full_with_consent";
type OnboardingConsentMode = "optional" | "required";

type StudySnapshot = {
  id: string;
  name: string;
  studyCode: string;
  description?: string;
  duration?: string;
  researcherName?: string;
  institution?: string;
};

type FormManualValues = {
  formReferenceName: string;
  onboardingConsentMode: OnboardingConsentMode;

  // Study Information
  studyTitle: string;
  piName: string;
  piCredentials: string;
  piEmail: string;
  piPhone: string;
  institution: string;
  studyPurpose: string;
  studyDuration: string;
  timeCommitment: string;
  expectedParticipants: string;

  // Procedures
  procedures: string;
  deviceRequirements: string;
  dataCollectionMethods: string;
  studyActivities: string;

  // Risks and Discomforts
  physicalRisks: string;
  privacyRisks: string;
  psychologicalRisks: string;
  dataBreachRisks: string;
  mitigationMeasures: string;

  // Benefits
  directBenefits: string;
  societalBenefits: string;
  noDirectBenefit: boolean;

  // Privacy and Confidentiality
  dataStorage: string;
  dataAccess: string;
  deidentification: string;
  hipaaCompliance: string;
  dataSharing: string;
  dataRetention: string;

  // Compensation
  compensate: boolean;
  compensationAmount: string;
  compensationSchedule: string;
  compensationStatement: string;

  // Voluntary Participation
  voluntaryText: string;
  withdrawalProcess: string;
  noPenalty: boolean;

  // Contact Information
  piContactForQuestions: string;
  irbContactForConcerns: string;
  studyCoordinatorContact: string;

  // IRB Approval
  irbName: string;
  irbNumber: string;
  irbApprovalDate: string;
  irbExpirationDate: string;

  // Consent & Signature
  consentStatement: string;
  requireDrawnSignature: boolean;

  // Data sharing plan (used in privacy section)
  dataSharingPlan: DataSharingPlan;
  dataSharingNotes: string;
};

type BackendSection = {
  key: string;
  title: string;
  bodyHtml: string;
  order: number;
};

type SaveActivateResponse = {
  ok: boolean;
  form?: {
    form_id: string;
    form_name: string;
    study_form_version: number;
    is_active: boolean;
  };
  previewUrl?: string;
  preview?: { url?: string };
};

type SourceFormDetail = {
  form_id: string;
  form_name: string;
  form_version_name: string | null;
  study_form_version: number | null;
  form_version: number | string;
  configuration_data?: Record<string, any> | null;
  form_content?: Record<string, any> | null;
  require_drawn_signature?: boolean | null;
  onboarding_consent_mode?: OnboardingConsentMode | null;
};

const API_BASE = API_CONFIG.BASE_URL.replace(/\/$/, "");

const STEP_LABELS = [
  "Study Information",
  "Procedures",
  "Risks & Benefits",
  "Privacy",
  "Voluntary",
  "Contacts",
  "IRB",
  "Consent & Signature",
];

const DATA_SHARING_OPTIONS: { value: DataSharingPlan; label: string }[] = [
  { value: "none", label: "No data sharing" },
  { value: "deidentified", label: "De-identified sharing" },
  { value: "full_with_consent", label: "Full sharing with explicit consent" },
];

const emptyManualValues: FormManualValues = {
  formReferenceName: "",
  onboardingConsentMode: "optional",
  studyTitle: "",
  piName: "",
  piCredentials: "",
  piEmail: "",
  piPhone: "",
  institution: "",
  studyPurpose: "",
  studyDuration: "",
  timeCommitment: "",
  expectedParticipants: "",

  procedures: "",
  deviceRequirements: "",
  dataCollectionMethods: "",
  studyActivities: "",

  physicalRisks: "",
  privacyRisks: "",
  psychologicalRisks: "",
  dataBreachRisks: "",
  mitigationMeasures: "",

  directBenefits: "",
  societalBenefits: "",
  noDirectBenefit: false,

  dataStorage: "",
  dataAccess: "",
  deidentification: "",
  hipaaCompliance: "",
  dataSharing: "",
  dataRetention: "",

  compensate: false,
  compensationAmount: "",
  compensationSchedule: "",
  compensationStatement: "",

  voluntaryText: "",
  withdrawalProcess: "",
  noPenalty: true,

  piContactForQuestions: "",
  irbContactForConcerns: "",
  studyCoordinatorContact: "",

  irbName: "",
  irbNumber: "",
  irbApprovalDate: "",
  irbExpirationDate: "",

  consentStatement:
    "I have read and understand the above information. I have had the opportunity to ask questions and have received satisfactory answers. I agree to participate in this study.",
  requireDrawnSignature: false,

  dataSharingPlan: "none",
  dataSharingNotes: "",
};

function valuesFromSourceForm(
  source: SourceFormDetail,
  current: FormManualValues,
): FormManualValues {
  const cfg = source.configuration_data || {};
  const sourceName = source.form_version_name || source.form_name;
  const nextReference = sourceName
    ? `${sourceName} (edited)`
    : current.formReferenceName;

  return {
    ...current,
    formReferenceName: nextReference,
    onboardingConsentMode:
      source.onboarding_consent_mode === "required" ||
      cfg.onboardingConsentMode === "required"
        ? "required"
        : "optional",
    piName: cfg.piName || current.piName,
    piCredentials: cfg.piCredentials || current.piCredentials,
    piEmail: cfg.piEmail || current.piEmail,
    piPhone: cfg.piPhone || current.piPhone,
    institution: cfg.institution || current.institution,
    studyPurpose: cfg.studyPurpose || current.studyPurpose,
    studyDuration: cfg.studyDuration || current.studyDuration,
    timeCommitment: cfg.timeCommitment || current.timeCommitment,
    expectedParticipants: cfg.expectedParticipants || current.expectedParticipants,
    procedures: cfg.procedures || current.procedures,
    deviceRequirements: cfg.deviceRequirements || current.deviceRequirements,
    dataCollectionMethods: cfg.dataCollectionMethods || current.dataCollectionMethods,
    studyActivities: cfg.studyActivities || current.studyActivities,
    physicalRisks: cfg.risks?.physical || current.physicalRisks,
    privacyRisks: cfg.risks?.privacy || current.privacyRisks,
    psychologicalRisks: cfg.risks?.psychological || current.psychologicalRisks,
    dataBreachRisks: cfg.risks?.dataBreach || current.dataBreachRisks,
    mitigationMeasures: cfg.risks?.mitigation || current.mitigationMeasures,
    directBenefits: cfg.benefits?.direct || current.directBenefits,
    societalBenefits: cfg.benefits?.societal || current.societalBenefits,
    noDirectBenefit:
      typeof cfg.benefits?.noDirectBenefit === "boolean"
        ? cfg.benefits.noDirectBenefit
        : current.noDirectBenefit,
    dataStorage: cfg.privacy?.dataStorage || current.dataStorage,
    dataAccess: cfg.privacy?.dataAccess || current.dataAccess,
    deidentification: cfg.privacy?.deidentification || current.deidentification,
    hipaaCompliance: cfg.privacy?.hipaaCompliance || current.hipaaCompliance,
    dataSharing: cfg.privacy?.dataSharing || current.dataSharing,
    dataRetention: cfg.privacy?.dataRetention || current.dataRetention,
    dataSharingPlan: cfg.privacy?.dataSharingPlan || current.dataSharingPlan,
    dataSharingNotes: cfg.privacy?.dataSharingNotes || current.dataSharingNotes,
    compensate:
      typeof cfg.compensation?.compensate === "boolean"
        ? cfg.compensation.compensate
        : current.compensate,
    compensationAmount: cfg.compensation?.amount || current.compensationAmount,
    compensationSchedule: cfg.compensation?.schedule || current.compensationSchedule,
    compensationStatement: cfg.compensation?.statement || current.compensationStatement,
    voluntaryText: cfg.voluntary?.text || current.voluntaryText,
    withdrawalProcess: cfg.voluntary?.withdrawalProcess || current.withdrawalProcess,
    noPenalty:
      typeof cfg.voluntary?.noPenalty === "boolean"
        ? cfg.voluntary.noPenalty
        : current.noPenalty,
    piContactForQuestions: cfg.contacts?.pi || current.piContactForQuestions,
    irbContactForConcerns: cfg.contacts?.irb || current.irbContactForConcerns,
    studyCoordinatorContact:
      cfg.contacts?.coordinator || current.studyCoordinatorContact,
    irbName: cfg.irb?.name || current.irbName,
    irbNumber: cfg.irb?.number || current.irbNumber,
    irbApprovalDate: cfg.irb?.approvalDate || current.irbApprovalDate,
    irbExpirationDate: cfg.irb?.expirationDate || current.irbExpirationDate,
    consentStatement: cfg.consentStatement || current.consentStatement,
    requireDrawnSignature:
      typeof source.require_drawn_signature === "boolean"
        ? source.require_drawn_signature
        : Boolean(cfg.requireDrawnSignature ?? current.requireDrawnSignature),
  };
}

const fieldRequired = (v: string) => !v || !v.trim();

function validateStep(
  step: number,
  values: FormManualValues,
): Record<string, string> {
  const e: Record<string, string> = {};
  switch (step) {
    case 0:
      if (fieldRequired(values.formReferenceName))
        e.formReferenceName = "Form reference name is required";
      if (fieldRequired(values.piName)) e.piName = "PI name is required";
      if (fieldRequired(values.institution))
        e.institution = "Institution is required";
      if (
        fieldRequired(values.piEmail) ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.piEmail.trim())
      )
        e.piEmail = "A valid PI email is required";
      if (fieldRequired(values.studyPurpose))
        e.studyPurpose = "Study purpose is required";
      break;
    case 1:
      if (
        fieldRequired(values.procedures) &&
        fieldRequired(values.studyActivities)
      )
        e.procedures = "Describe what participants will be asked to do";
      break;
    case 2:
      if (
        !values.physicalRisks &&
        !values.privacyRisks &&
        !values.psychologicalRisks &&
        !values.dataBreachRisks
      )
        e.physicalRisks = "Document at least one risk category";
      if (
        values.noDirectBenefit === false &&
        fieldRequired(values.directBenefits)
      )
        e.directBenefits =
          "Describe direct benefits, or mark 'no direct benefit'";
      break;
    case 3:
      if (fieldRequired(values.dataStorage))
        e.dataStorage = "Describe how data will be stored and protected";
      if (fieldRequired(values.dataAccess))
        e.dataAccess = "Describe who will have access to data";
      break;
    case 4:
      if (fieldRequired(values.voluntaryText))
        e.voluntaryText = "Describe the voluntary nature of participation";
      break;
    case 5:
      if (
        fieldRequired(values.piContactForQuestions) &&
        fieldRequired(values.piEmail)
      )
        e.piContactForQuestions = "PI contact is required";
      break;
    case 6:
      if (fieldRequired(values.irbNumber))
        e.irbNumber = "IRB approval number is required";
      if (fieldRequired(values.irbName)) e.irbName = "IRB name is required";
      break;
    case 7:
      break;
  }
  return e;
}

function escapeHtml(input: unknown) {
  if (input === null || input === undefined) return "";
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function nl2br(value: string) {
  return escapeHtml(value).replace(/\n/g, "<br/>");
}

function generatePreviewHtml(
  sections: BackendSection[],
  studyName: string,
  requireDrawnSignature: boolean,
) {
  const wrap = (title: string, body: string) => `
    <section style="padding:10px 0; border-top:1px solid #e5e7eb">
      <h3 style="margin:0 0 4px 0; font-size:14px; color:#6b7280">${escapeHtml(title)}</h3>
      <div style="font-size:13px; line-height:1.5; color:#374151">${body}</div>
    </section>`;

  const sectionsHtml = sections
    .map((s) => wrap(s.title, s.bodyHtml))
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Preview - ${escapeHtml(studyName)}</title>
  <style>
    body { margin:0; padding:16px; font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif; background:#f3f4f6; }
    article { max-width:720px; margin:0 auto; background:#fff; padding:24px; border-radius:12px; border:1px solid #e5e7eb; }
    header { border-bottom:2px solid #990000; padding-bottom:8px; margin-bottom:10px; }
    header h2 { margin:0; font-size:18px; color:#990000; }
    .consent-section { padding:10px 0; border-top:1px solid #e5e7eb; }
    .consent-section h3 { margin:0 0 4px 0; font-size:14px; color:#6b7280; }
    .consent-section div { font-size:13px; line-height:1.5; color:#374151; }
    ol { margin:0; padding-left:18px; font-size:13px; color:#374151; }
  </style>
</head>
<body>
  <article>
    <header>
      <h2>StudySync — ${escapeHtml(studyName)}</h2>
    </header>
    ${sectionsHtml}
    <section class="consent-section">
      <h3>Consent &amp; Signature</h3>
      <ol>
        <li>I agree to participate / I do not agree</li>
        <li>Typed name (required)</li>
        <li>Date (required)</li>
        <li>Drawn signature (${requireDrawnSignature ? "required" : "optional"})</li>
      </ol>
    </section>
  </article>
</body>
</html>`;
}

function composeManualSections(
  study: StudySnapshot | null,
  values: FormManualValues,
): BackendSection[] {
  const studyTitle = study?.name || "Untitled Study";
  const institution = values.institution || study?.institution || "";

  const infoHtml = [
    `<h3 style="margin:0 0 .5rem 0">${escapeHtml(studyTitle)}</h3>`,
    `<p><strong>Principal Investigator:</strong> ${escapeHtml(values.piName)}${
      values.piCredentials ? `, ${escapeHtml(values.piCredentials)}` : ""
    }${values.piEmail ? ` (${escapeHtml(values.piEmail)})` : ""}${
      values.piPhone ? ` &middot; ${escapeHtml(values.piPhone)}` : ""
    }</p>`,
    institution
      ? `<p><strong>Institution:</strong> ${escapeHtml(institution)}</p>`
      : "",
    `<h4>Purpose of the study</h4>`,
    `<p>${nl2br(values.studyPurpose || "")}</p>`,
    values.studyDuration
      ? `<p><strong>Study Duration:</strong> ${escapeHtml(values.studyDuration)}</p>`
      : "",
    values.timeCommitment
      ? `<p><strong>Time Commitment:</strong> ${escapeHtml(values.timeCommitment)}</p>`
      : "",
    values.expectedParticipants
      ? `<p><strong>Expected Participants:</strong> ${escapeHtml(values.expectedParticipants)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const proceduresHtml = [
    `<h4>Study Activities and Timeline</h4>`,
    `<p>${nl2br(values.studyActivities || values.procedures || "")}</p>`,
    values.procedures && values.studyActivities
      ? `<h4>What you will be asked to do</h4><p>${nl2br(values.procedures)}</p>`
      : "",
    values.deviceRequirements
      ? `<h4>Device Requirements</h4><p>${nl2br(values.deviceRequirements)}</p>`
      : "",
    values.dataCollectionMethods
      ? `<h4>Data Collection Methods</h4><p>${nl2br(values.dataCollectionMethods)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const risksList = [
    values.physicalRisks
      ? `<p><strong>Physical risks:</strong> ${nl2br(values.physicalRisks)}</p>`
      : "",
    values.privacyRisks
      ? `<p><strong>Privacy risks:</strong> ${nl2br(values.privacyRisks)}</p>`
      : "",
    values.psychologicalRisks
      ? `<p><strong>Psychological risks:</strong> ${nl2br(values.psychologicalRisks)}</p>`
      : "",
    values.dataBreachRisks
      ? `<p><strong>Data breach risks:</strong> ${nl2br(values.dataBreachRisks)}</p>`
      : "",
    values.mitigationMeasures
      ? `<p><strong>Mitigation measures:</strong> ${nl2br(values.mitigationMeasures)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const benefitsHtml = [
    `<h4>Benefits</h4>`,
    values.noDirectBenefit
      ? `<p><em>You will not directly benefit from participation in this study.</em></p>`
      : values.directBenefits
        ? `<p><strong>Direct benefits to you:</strong> ${nl2br(values.directBenefits)}</p>`
        : "",
    values.societalBenefits
      ? `<p><strong>Benefits to society/science:</strong> ${nl2br(values.societalBenefits)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const risksAndBenefitsHtml =
    `<h4>Risks and Discomforts</h4>${risksList || "<p>No notable risks beyond those described above.</p>"}` +
    `<hr style="margin:.75rem 0"/>` +
    benefitsHtml;

  const dsNote =
    values.dataSharingPlan !== "none" && values.dataSharingNotes
      ? ` <em>(${escapeHtml(values.dataSharingNotes)})</em>`
      : "";
  const dsLine =
    values.dataSharingPlan === "deidentified"
      ? "De-identified data may be shared for research purposes."
      : values.dataSharingPlan === "full_with_consent"
        ? "Your data may be shared in full with your explicit consent for specified research."
        : "";

  const privacyHtml = [
    values.dataStorage
      ? `<p><strong>How data will be stored and protected:</strong> ${nl2br(values.dataStorage)}</p>`
      : "",
    values.dataAccess
      ? `<p><strong>Who will have access to data:</strong> ${nl2br(values.dataAccess)}</p>`
      : "",
    values.deidentification
      ? `<p><strong>De-identification procedures:</strong> ${nl2br(values.deidentification)}</p>`
      : "",
    values.hipaaCompliance
      ? `<p><strong>HIPAA compliance:</strong> ${nl2br(values.hipaaCompliance)}</p>`
      : "",
    values.dataSharing
      ? `<p><strong>Data sharing plans:</strong> ${nl2br(values.dataSharing)}</p>`
      : "",
    dsLine
      ? `<p><strong>Data sharing plan:</strong> ${escapeHtml(dsLine)}${dsNote}</p>`
      : "",
    values.dataRetention
      ? `<p><strong>Data retention period:</strong> ${escapeHtml(values.dataRetention)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const voluntaryHtml = [
    `<p>${nl2br(
      values.voluntaryText ||
        "Participation in this study is voluntary. You may decide not to participate or withdraw at any time without penalty or loss of benefits to which you are otherwise entitled.",
    )}</p>`,
    values.withdrawalProcess
      ? `<p><strong>Process for withdrawal:</strong> ${nl2br(values.withdrawalProcess)}</p>`
      : "",
    values.noPenalty
      ? `<p><em>There is no penalty for withdrawing from this study.</em></p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  const compensationHtml = values.compensate
    ? `<p><strong>Compensation:</strong> ${escapeHtml(
        values.compensationAmount || "Amount to be determined",
      )}${
        values.compensationSchedule
          ? ` (${escapeHtml(values.compensationSchedule)})`
          : ""
      }</p>`
    : `<p><strong>Compensation:</strong> ${
        values.compensationStatement ||
        "You will not be paid for participation in this study."
      }</p>`;

  const contactsHtml = [
    `<p><strong>For questions about the study, contact:</strong> ${nl2br(
      values.piContactForQuestions ||
        `${values.piName || "Principal Investigator"}${
          values.piEmail ? ` (${values.piEmail})` : ""
        }${values.piPhone ? `, ${values.piPhone}` : ""}`,
    )}</p>`,
    values.studyCoordinatorContact
      ? `<p><strong>Study coordinator:</strong> ${nl2br(values.studyCoordinatorContact)}</p>`
      : "",
    values.irbContactForConcerns
      ? `<p><strong>For concerns about your rights as a research participant, contact the IRB:</strong> ${nl2br(
          values.irbContactForConcerns,
        )}</p>`
      : "",
    compensationHtml,
  ]
    .filter(Boolean)
    .join("\n");

  const irbHtml = [
    values.irbName
      ? `<p><strong>IRB:</strong> ${escapeHtml(values.irbName)}</p>`
      : "",
    values.irbNumber
      ? `<p><strong>IRB Approval Number:</strong> ${escapeHtml(values.irbNumber)}</p>`
      : "",
    values.irbApprovalDate
      ? `<p><strong>IRB Approval Date:</strong> ${escapeHtml(values.irbApprovalDate)}</p>`
      : "",
    values.irbExpirationDate
      ? `<p><strong>IRB Expiration Date:</strong> ${escapeHtml(values.irbExpirationDate)}</p>`
      : "",
  ]
    .filter(Boolean)
    .join("\n");

  return [
    {
      key: "study_information",
      title: "Study Information",
      order: 1,
      bodyHtml: infoHtml,
    },
    {
      key: "procedures",
      title: "Procedures",
      order: 2,
      bodyHtml: proceduresHtml,
    },
    {
      key: "risks_and_benefits",
      title: "Risks and Benefits",
      order: 3,
      bodyHtml: risksAndBenefitsHtml,
    },
    {
      key: "privacy_and_confidentiality",
      title: "Privacy and Confidentiality",
      order: 4,
      bodyHtml: privacyHtml,
    },
    {
      key: "voluntary_participation",
      title: "Voluntary Participation",
      order: 5,
      bodyHtml: voluntaryHtml,
    },
    {
      key: "contact_information",
      title: "Contact Information",
      order: 6,
      bodyHtml: contactsHtml,
    },
    {
      key: "irb_approval",
      title: "IRB Approval",
      order: 7,
      bodyHtml: irbHtml,
    },
  ];
}

function validateRequired(
  values: FormManualValues,
  study: StudySnapshot | null,
) {
  const errors: Record<string, string> = {};

  if (fieldRequired(values.formReferenceName)) {
    errors.formReferenceName = "Form reference name is required";
  }
  if (fieldRequired(values.piName)) errors.piName = "PI name is required";
  if (fieldRequired(values.institution))
    errors.institution = "Institution is required";
  if (
    fieldRequired(values.piEmail) ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.piEmail.trim())
  )
    errors.piEmail = "A valid PI email is required";

  if (fieldRequired(values.studyPurpose))
    errors.studyPurpose = "Study purpose is required";

  if (fieldRequired(values.procedures) && fieldRequired(values.studyActivities))
    errors.procedures = "Describe what participants will be asked to do";

  if (
    !values.physicalRisks &&
    !values.privacyRisks &&
    !values.psychologicalRisks &&
    !values.dataBreachRisks
  )
    errors.physicalRisks = "Document at least one risk category";

  if (values.noDirectBenefit === false && fieldRequired(values.directBenefits))
    errors.directBenefits =
      "Describe direct benefits, or mark 'no direct benefit'";

  if (fieldRequired(values.dataStorage))
    errors.dataStorage = "Describe how data will be stored and protected";
  if (fieldRequired(values.dataAccess))
    errors.dataAccess = "Describe who will have access to data";

  if (fieldRequired(values.voluntaryText))
    errors.voluntaryText = "Describe the voluntary nature of participation";

  if (
    fieldRequired(values.piContactForQuestions) &&
    fieldRequired(values.piEmail)
  )
    errors.piContactForQuestions = "PI contact is required";

  if (fieldRequired(values.irbNumber))
    errors.irbNumber = "IRB approval number is required";
  if (fieldRequired(values.irbName)) errors.irbName = "IRB name is required";

  return errors;
}

async function requestJson<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = await apiService.getAuthToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const text = await response.text();
  const json = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(
      json?.error || json?.message || `Request failed (${response.status})`,
    );
  }
  return json as T;
}

export default function ConsentFormCreatePage() {
  const navigate = useNavigate();
  const { studyId: routeStudyId } = useParams<{ studyId: string }>();
  const studyId = routeStudyId || "";
  const urlParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const sourceFormId = urlParams.get("sourceFormId") || "";

  const [study, setStudy] = React.useState<StudySnapshot | null>(null);
  const [loadingStudy, setLoadingStudy] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [existingFormCount, setExistingFormCount] = React.useState(0);
  const [sourceFormName, setSourceFormName] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<CreateMode>("template");
  const [values, setValues] =
    React.useState<FormManualValues>(emptyManualValues);
  const [manualState, setManualState] = React.useState<ManualConsentState>(() =>
    createDefaultManualState(),
  );
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [dialogAction, setDialogAction] = React.useState<
    "preview" | "create" | "cancel"
  >("create");
  const [submitting, setSubmitting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = React.useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  const nextVersion = existingFormCount + 1;
  const sections = React.useMemo(
    () => composeManualSections(study, values),
    [study, values],
  );
  const sourceModeLocked = Boolean(sourceFormId);

  React.useEffect(() => {
    if (!studyId) return;
    let cancelled = false;

    const loadAll = async () => {
      setLoadingStudy(true);
      setLoadError(null);
      try {
        const studyData = await requestJson<any>(
          `/api/studies/${encodeURIComponent(studyId)}`,
        );
        const studyObj: StudySnapshot = {
          id: studyData?.study?.id ?? studyData?.id ?? studyId,
          name: studyData?.study?.name ?? studyData?.name ?? "",
          studyCode: studyData?.study?.studyCode ?? studyData?.studyCode ?? "",
          description:
            studyData?.study?.description ?? studyData?.description ?? "",
          duration: studyData?.study?.duration ?? studyData?.duration ?? "",
          researcherName:
            studyData?.study?.researcherName ?? studyData?.researcherName ?? "",
          institution:
            studyData?.study?.institution ?? studyData?.institution ?? "",
        };

        const formsData = await requestJson<{ items: any[]; total: number }>(
          `/api/consent/researcher/forms?studyId=${encodeURIComponent(studyId)}`,
        );

        let sourceForm: SourceFormDetail | null = null;
        if (sourceFormId) {
          const sourceData = await requestJson<{ ok: boolean; form: SourceFormDetail }>(
            `/api/consent/researcher/forms/${encodeURIComponent(sourceFormId)}?studyId=${encodeURIComponent(studyId)}`,
          );
          sourceForm = sourceData.form || null;
        }

        if (cancelled) return;
        const sourceCreationMode: CreateMode =
          sourceForm?.configuration_data?.creationMode === "manual"
            ? "manual"
            : "template";
        setMode(sourceCreationMode);
        if (sourceCreationMode === "manual" && sourceForm) {
          setManualState(
            manualStateFromFormContent(
              sourceForm.form_content,
              sourceForm.configuration_data,
            ),
          );
        }
        setStudy(studyObj);
        setExistingFormCount(formsData?.items?.length || 0);
        setSourceFormName(
          sourceForm
            ? sourceForm.form_version_name || sourceForm.form_name || "selected form"
            : null,
        );
        setValues((current) => {
          const prefilled = sourceForm
            ? valuesFromSourceForm(sourceForm, current)
            : current;
          return {
            ...prefilled,
            studyTitle: current.studyTitle || studyObj.name,
            formReferenceName: sourceForm
              ? prefilled.formReferenceName
              : current.formReferenceName ||
                `Version ${(formsData?.items?.length || 0) + 1}`,
            institution: prefilled.institution || studyObj.institution || "",
          };
        });
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || "Failed to load study");
        }
      } finally {
        if (!cancelled) setLoadingStudy(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [studyId, sourceFormId]);

  const updateField = <K extends keyof FormManualValues>(
    key: K,
    value: FormManualValues[K],
  ) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: CreateMode | null,
  ) => {
    if (!next || sourceModeLocked) return;
    setMode(next);
    setErrors({});
    setActionError(null);
  };

  const handleNext = () => {
    const stepErrors = validateStep(activeStep, values);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;
    if (activeStep < STEP_LABELS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setErrors({});
      setActiveStep(activeStep - 1);
    }
  };

  const handleOpenConfirm = (action: "preview" | "create" | "cancel") => {
    if (action !== "cancel") {
      const validation =
        mode === "template"
          ? validateRequired(values, study)
          : {
              ...(fieldRequired(values.formReferenceName)
                ? { formReferenceName: "Form reference name is required" }
                : {}),
              ...validateManualConsentState(manualState),
            };
      setErrors(validation);
      if (Object.keys(validation).length > 0) {
        setActionError(
          "Please complete the required fields highlighted in red.",
        );
        return;
      }
    }
    setActionError(null);
    setDialogAction(action);
    setConfirmOpen(true);
  };

  const handleCancelNav = () => {
    handleOpenConfirm("cancel");
  };

  const submitCreate = async (action: "save" | "preview") => {
    setSubmitting(true);
    setActionError(null);
    setActionSuccess(null);
    setPreviewUrl(null);
    try {
      const manualNormalized = normalizeManualState(manualState);
      const backendSections =
        mode === "manual"
          ? manualSectionsToBackendSections(manualState)
          : sections.filter((s) => s.bodyHtml.trim());
      const payload =
        mode === "manual"
          ? {
              studyId,
              title: values.formReferenceName || study?.name || "Untitled Study",
              language: "EN",
              formType: "manual",
              creationMode: "manual",
              requireDrawnSignature: values.requireDrawnSignature,
              onboardingConsentMode: values.onboardingConsentMode,
              sections: backendSections,
              studySections: manualNormalized.studySections,
              participantSections: manualNormalized.participantSections,
              consentStatement: manualNormalized.consentStatement,
              configuration: {
                creationMode: "manual",
                onboardingConsentMode: values.onboardingConsentMode,
                studySections: manualNormalized.studySections,
                participantSections: manualNormalized.participantSections,
                consentStatement: manualNormalized.consentStatement,
                requireDrawnSignature: values.requireDrawnSignature,
              },
            }
          : {
              studyId,
              title: values.formReferenceName || study?.name || "Untitled Study",
              language: "EN",
              formType: "default_template",
              creationMode: "template",
              requireDrawnSignature: values.requireDrawnSignature,
              onboardingConsentMode: values.onboardingConsentMode,
              sections: backendSections,
              configuration: {
                creationMode: "template",
                onboardingConsentMode: values.onboardingConsentMode,
                piName: values.piName,
                piCredentials: values.piCredentials,
                piEmail: values.piEmail,
                piPhone: values.piPhone,
                institution: values.institution,
                studyTitle: values.studyTitle,
                studyPurpose: values.studyPurpose,
                studyDuration: values.studyDuration,
                timeCommitment: values.timeCommitment,
                expectedParticipants: values.expectedParticipants,
                procedures: values.procedures,
                deviceRequirements: values.deviceRequirements,
                dataCollectionMethods: values.dataCollectionMethods,
                studyActivities: values.studyActivities,
                risks: {
                  physical: values.physicalRisks,
                  privacy: values.privacyRisks,
                  psychological: values.psychologicalRisks,
                  dataBreach: values.dataBreachRisks,
                  mitigation: values.mitigationMeasures,
                },
                benefits: {
                  direct: values.directBenefits,
                  societal: values.societalBenefits,
                  noDirectBenefit: values.noDirectBenefit,
                },
                privacy: {
                  dataStorage: values.dataStorage,
                  dataAccess: values.dataAccess,
                  deidentification: values.deidentification,
                  hipaaCompliance: values.hipaaCompliance,
                  dataSharing: values.dataSharing,
                  dataRetention: values.dataRetention,
                  dataSharingPlan: values.dataSharingPlan,
                  dataSharingNotes: values.dataSharingNotes,
                },
                compensation: {
                  compensate: values.compensate,
                  amount: values.compensationAmount,
                  schedule: values.compensationSchedule,
                  statement: values.compensationStatement,
                },
                voluntary: {
                  text: values.voluntaryText,
                  withdrawalProcess: values.withdrawalProcess,
                  noPenalty: values.noPenalty,
                },
                contacts: {
                  pi: values.piContactForQuestions,
                  irb: values.irbContactForConcerns,
                  coordinator: values.studyCoordinatorContact,
                },
                irb: {
                  name: values.irbName,
                  number: values.irbNumber,
                  approvalDate: values.irbApprovalDate,
                  expirationDate: values.irbExpirationDate,
                },
                consentStatement: values.consentStatement,
              },
            };

      const endpoint =
        action === "preview"
          ? "/api/consent/researcher/template/preview"
          : "/api/consent/researcher/template/save-activate";

      await requestJson<SaveActivateResponse>(endpoint, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setConfirmOpen(false);

      if (action === "preview") {
        const previewHtml = generatePreviewHtml(
          backendSections,
          study?.name || values.formReferenceName || "Untitled Study",
          values.requireDrawnSignature,
        );
        const blob = new Blob([previewHtml], {
          type: "text/html;charset=utf-8",
        });
        const dataUrl = URL.createObjectURL(blob);
        const previewWindow = window.open(
          dataUrl,
          "_blank",
          "noopener,noreferrer",
        );
        if (previewWindow) {
          setPreviewUrl(dataUrl);
          previewWindow.addEventListener("beforeunload", () => {
            URL.revokeObjectURL(dataUrl);
          });
        } else {
          setPreviewUrl(dataUrl);
        }
        setActionSuccess("Preview opened in a new tab.");
        return;
      }

      navigate(
        `/researcher/study/${encodeURIComponent(studyId)}/consent-management`,
      );
    } catch (err: any) {
      setActionError(err?.message || "Failed to create consent form");
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (
    name: keyof FormManualValues,
    label: string,
    options: {
      multiline?: boolean;
      rows?: number;
      required?: boolean;
      helperText?: string;
      placeholder?: string;
      type?: string;
      select?: boolean;
      selectOptions?: { value: string; label: string }[];
      inputProps?: any;
    } = {},
  ) => {
    const {
      multiline,
      rows,
      required,
      helperText,
      placeholder,
      type,
      select,
      selectOptions,
      inputProps,
    } = options;
    const value = (values[name] as string) ?? "";
    const err = errors[name as string];
    return (
      <TextField
        label={label}
        value={value}
        onChange={(event) => updateField(name, event.target.value as any)}
        fullWidth
        required={required}
        error={Boolean(err)}
        helperText={err || helperText}
        placeholder={placeholder}
        type={type || (multiline ? undefined : "text")}
        multiline={multiline}
        rows={multiline ? rows || 3 : undefined}
        select={select}
        inputProps={inputProps}
        InputLabelProps={
          type === "date" || type === "datetime-local"
            ? { shrink: true }
            : undefined
        }
      >
        {select &&
          selectOptions?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </TextField>
    );
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Study title"
                value={study?.name || "Untitled Study"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={{ "& .MuiInputBase-root": { bgcolor: "action.hover" } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("institution", "Institution", {
                required: true,
                placeholder: "Stanford University",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("piName", "Principal investigator name", {
                required: true,
                placeholder: "Dr. Jane Doe",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("piCredentials", "PI credentials", {
                placeholder: "PhD, MD",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("piEmail", "PI email", {
                required: true,
                type: "email",
                placeholder: "jane@stanford.edu",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("piPhone", "PI phone", {
                placeholder: "(650) 555-1234",
              })}
            </Grid>
            <Grid item xs={12}>
              {renderField("studyPurpose", "Study purpose (1-2 paragraphs)", {
                required: true,
                multiline: true,
                rows: 4,
                helperText:
                  "Explain the goal of the study, why it matters, and what is being investigated.",
              })}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderField("studyDuration", "Study duration", {
                placeholder: "e.g., 6 months",
              })}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderField("timeCommitment", "Time commitment", {
                placeholder: "e.g., Wear device 24/7; weekly surveys",
              })}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderField("expectedParticipants", "Expected participants", {
                type: "number",
                inputProps: { min: 0 },
                placeholder: "e.g., 100",
              })}
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderField(
                "procedures",
                "What participants will be asked to do",
                {
                  required: true,
                  multiline: true,
                  rows: 4,
                },
              )}
            </Grid>
            <Grid item xs={12}>
              {renderField("studyActivities", "Study activities and timeline", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField(
                "deviceRequirements",
                "Device requirements (e.g., wear Fitbit continuously)",
                { multiline: true, rows: 3 },
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("dataCollectionMethods", "Data collection methods", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderField(
                "physicalRisks",
                "Physical risks (minimal for wearable studies)",
                { multiline: true, rows: 3 },
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("privacyRisks", "Privacy risks", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("psychologicalRisks", "Psychological risks", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("dataBreachRisks", "Data breach risks", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12}>
              {renderField("mitigationMeasures", "Mitigation measures", {
                multiline: true,
                rows: 3,
                helperText:
                  "Describe the safeguards in place to minimize the risks above.",
              })}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Benefits
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Does the participant receive a direct benefit?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Select "No" if participation offers no direct benefit to the
                  participant.
                </Typography>
                <ToggleButtonGroup
                  value={values.noDirectBenefit ? "no" : "yes"}
                  exclusive
                  onChange={(_e, val) =>
                    val && updateField("noDirectBenefit", (val === "no") as any)
                  }
                  sx={{
                    borderRadius: 999,
                    p: 0.5,
                    backgroundColor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    "& .MuiToggleButton-root": {
                      border: 0,
                      borderRadius: "999px !important",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      fontWeight: 500,
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    },
                  }}
                >
                  <ToggleButton value="yes">
                    Yes — there are direct benefits
                  </ToggleButton>
                  <ToggleButton value="no">No — no direct benefit</ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            </Grid>
            {!values.noDirectBenefit && (
              <Grid item xs={12} md={6}>
                {renderField(
                  "directBenefits",
                  "Direct benefits to participant (if any)",
                  { multiline: true, rows: 3 },
                )}
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              {renderField(
                "societalBenefits",
                "Benefits to society / science",
                {
                  multiline: true,
                  rows: 3,
                },
              )}
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderField(
                "dataStorage",
                "How data will be stored and protected",
                { required: true, multiline: true, rows: 3 },
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("dataAccess", "Who will have access to data", {
                required: true,
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("deidentification", "De-identification procedures", {
                multiline: true,
                rows: 3,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField(
                "hipaaCompliance",
                "HIPAA compliance language (if applicable)",
                {
                  multiline: true,
                  rows: 3,
                  helperText:
                    "Include any HIPAA-related language, or leave blank if not applicable.",
                },
              )}
            </Grid>
            <Grid item xs={12}>
              {renderField("dataSharing", "Data sharing plans", {
                multiline: true,
                rows: 2,
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("dataRetention", "Data retention period", {
                placeholder: "e.g., 5 years after publication",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("dataSharingPlan", "Data sharing plan", {
                select: true,
                selectOptions: DATA_SHARING_OPTIONS,
              })}
            </Grid>
            {values.dataSharingPlan !== "none" && (
              <Grid item xs={12}>
                {renderField(
                  "dataSharingNotes",
                  "Data sharing notes (optional)",
                  {
                    multiline: true,
                    rows: 2,
                  },
                )}
              </Grid>
            )}
          </Grid>
        );
      case 4:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderField(
                "voluntaryText",
                "Voluntary participation statement",
                {
                  required: true,
                  multiline: true,
                  rows: 3,
                  helperText:
                    "Explain that participation is voluntary and that the participant can withdraw at any time.",
                },
              )}
            </Grid>
            <Grid item xs={12}>
              {renderField("withdrawalProcess", "Process for withdrawal", {
                multiline: true,
                rows: 3,
                helperText:
                  "Describe how a participant can withdraw and what happens to their data.",
              })}
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Is there a penalty for withdrawal?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Select "No" to confirm participants may withdraw without
                  penalty.
                </Typography>
                <ToggleButtonGroup
                  value={values.noPenalty ? "no_penalty" : "penalty"}
                  exclusive
                  onChange={(_e, val) =>
                    val &&
                    updateField("noPenalty", (val === "no_penalty") as any)
                  }
                  sx={{
                    borderRadius: 999,
                    p: 0.5,
                    backgroundColor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    "& .MuiToggleButton-root": {
                      border: 0,
                      borderRadius: "999px !important",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      fontWeight: 500,
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    },
                  }}
                >
                  <ToggleButton value="no_penalty">
                    No — no penalty
                  </ToggleButton>
                  <ToggleButton value="penalty">
                    Yes — there is a penalty
                  </ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Compensation
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Will participants be compensated?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Select "Yes" if the study offers payment, gift cards, or other
                  compensation.
                </Typography>
                <ToggleButtonGroup
                  value={values.compensate ? "yes" : "no"}
                  exclusive
                  onChange={(_e, val) =>
                    val && updateField("compensate", (val === "yes") as any)
                  }
                  sx={{
                    borderRadius: 999,
                    p: 0.5,
                    backgroundColor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    "& .MuiToggleButton-root": {
                      border: 0,
                      borderRadius: "999px !important",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      fontWeight: 500,
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    },
                  }}
                >
                  <ToggleButton value="yes">
                    Yes — participants are compensated
                  </ToggleButton>
                  <ToggleButton value="no">No — no compensation</ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            </Grid>
            {values.compensate ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderField("compensationAmount", "Compensation amount", {
                    placeholder: "$25 per visit",
                  })}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderField(
                    "compensationSchedule",
                    "Compensation schedule",
                    {
                      placeholder: "Monthly via gift card",
                    },
                  )}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                {renderField(
                  "compensationStatement",
                  "Compensation statement",
                  {
                    placeholder: "You will not be paid for participation.",
                  },
                )}
              </Grid>
            )}
          </Grid>
        );
      case 5:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderField(
                "piContactForQuestions",
                "PI contact for study questions",
                {
                  multiline: true,
                  rows: 3,
                  helperText:
                    "Defaults to the PI name and email above if left blank.",
                },
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField(
                "irbContactForConcerns",
                "IRB contact for participant rights",
                {
                  multiline: true,
                  rows: 3,
                },
              )}
            </Grid>
            <Grid item xs={12}>
              {renderField(
                "studyCoordinatorContact",
                "Study coordinator contact (if applicable)",
                { multiline: true, rows: 2 },
              )}
            </Grid>
          </Grid>
        );
      case 6:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderField("irbName", "IRB name", {
                required: true,
                placeholder: "Stanford IRB",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("irbNumber", "IRB approval number", {
                required: true,
                placeholder: "IRB-2025-XXXX",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField("irbApprovalDate", "IRB approval date", {
                type: "date",
              })}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderField(
                "irbExpirationDate",
                "IRB expiration date (if applicable)",
                {
                  type: "date",
                },
              )}
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" icon={<InfoOutlined />}>
                The IRB details appear at the bottom of the consent form and in
                the contact section for participants.
              </Alert>
            </Grid>
          </Grid>
        );
      case 7:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderField(
                "consentStatement",
                "Consent statement (shown before signature block)",
                {
                  multiline: true,
                  rows: 3,
                  helperText:
                    "Participants will see this statement before the agreement, signature, and date questions.",
                },
              )}
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Require drawn signature?
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.5 }}
                >
                  Typed name is always collected. Enable drawn signature when the study requires it.
                </Typography>
                <ToggleButtonGroup
                  value={values.requireDrawnSignature ? "yes" : "no"}
                  exclusive
                  onChange={(_e, val) =>
                    val &&
                    updateField("requireDrawnSignature", (val === "yes") as any)
                  }
                  sx={{
                    borderRadius: 999,
                    p: 0.5,
                    backgroundColor: "background.default",
                    border: "1px solid",
                    borderColor: "divider",
                    "& .MuiToggleButton-root": {
                      border: 0,
                      borderRadius: "999px !important",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      fontWeight: 500,
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { backgroundColor: "primary.dark" },
                      },
                    },
                  }}
                >
                  <ToggleButton value="yes">
                    Yes — require drawn signature
                  </ToggleButton>
                  <ToggleButton value="no">No — typed name only</ToggleButton>
                </ToggleButtonGroup>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Block 7 will collect
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle fontSize="small" color="primary" />
                    <Typography variant="body2">
                      Consent agreement (I agree / I do not agree)
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle fontSize="small" color="primary" />
                    <Typography variant="body2">
                      Typed legal name (required, validated as a name)
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle fontSize="small" color="primary" />
                    <Typography variant="body2">
                      Date (required, MM/DD/YYYY, pre-filled with today)
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle
                      fontSize="small"
                      color={values.requireDrawnSignature ? "primary" : "disabled"}
                    />
                    <Typography variant="body2">
                      Drawn signature box {values.requireDrawnSignature ? "required" : "shown when enabled"}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() =>
              navigate(
                `/researcher/study/${encodeURIComponent(studyId)}/consent-management`,
              )
            }
            sx={{ alignSelf: "flex-start" }}
          >
            Back to consent management
          </Button>
        </Stack>

        <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography
                  variant="overline"
                  color="primary"
                  sx={{ letterSpacing: 1.2, fontWeight: 600 }}
                >
                  {sourceFormId ? "Edit Consent Form" : "New Consent Form"}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {study?.name || "Loading study..."}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sourceFormId && sourceFormName
                    ? `Creating a new version based on ${sourceFormName}.`
                    : "Configure the consent form for this study."}{" "}
                  Version {nextVersion} will become the default only if this is
                  the first consent form for the study.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Study ID: ${studyId || "N/A"}`}
                  size="small"
                  variant="outlined"
                />
                {study?.studyCode && (
                  <Chip
                    label={`Study code: ${study.studyCode}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={`New version: v${nextVersion}`}
                  size="small"
                  color="primary"
                />
                <Chip
                  label={
                    existingFormCount === 0
                      ? "First version (auto-default)"
                      : `${existingFormCount} version${existingFormCount === 1 ? "" : "s"} exist`
                  }
                  size="small"
                  color={existingFormCount === 0 ? "success" : "default"}
                  variant="outlined"
                />
              </Stack>
            </Stack>
            <Divider />
            <TextField
              label="Form reference name"
              value={values.formReferenceName}
              onChange={(e) => updateField("formReferenceName", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.formReferenceName)}
              helperText={
                errors.formReferenceName ||
                "Give this consent form a unique name for easy identification"
              }
              placeholder="e.g., Fitbit Study v2 Consent"
            />
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Participants must complete consent approval during onboarding
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    When enabled, Fitbit onboarding cannot continue until this
                    consent version is completed.
                  </Typography>
                </Box>
                <Switch
                  checked={values.onboardingConsentMode === "required"}
                  onChange={(e) =>
                    updateField(
                      "onboardingConsentMode",
                      e.target.checked ? "required" : "optional",
                    )
                  }
                  inputProps={{ "aria-label": "Require consent during onboarding" }}
                />
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        {loadError && <Alert severity="error">{loadError}</Alert>}
        {actionError && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {actionSuccess && (
          <Alert
            severity="success"
            onClose={() => setActionSuccess(null)}
            action={
              previewUrl ? (
                <Button
                  color="inherit"
                  size="small"
                  endIcon={<OpenInNew fontSize="small" />}
                  onClick={() =>
                    window.open(previewUrl, "_blank", "noopener,noreferrer")
                  }
                >
                  Open preview
                </Button>
              ) : null
            }
          >
            {actionSuccess}
          </Alert>
        )}

        <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                How would you like to create this form?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the provided template wizard or build a fully custom manual form.
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              aria-label="Consent creation mode"
              sx={{
                borderRadius: 999,
                p: 0.5,
                backgroundColor: "background.default",
                border: "1px solid",
                borderColor: "divider",
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: "999px !important",
                  px: 2.5,
                  py: 0.75,
                  textTransform: "none",
                  fontWeight: 500,
                  color: "text.secondary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="template" disabled={sourceModeLocked && mode !== "template"}>
                <DescriptionOutlined fontSize="small" sx={{ mr: 1 }} /> Provided template
              </ToggleButton>
              <ToggleButton value="manual" disabled={sourceModeLocked && mode !== "manual"}>
                <Edit fontSize="small" sx={{ mr: 1 }} /> Manual builder
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Paper>

        {mode === "manual" ? (
          <>
            <ManualConsentBuilder
              state={manualState}
              onChange={setManualState}
              errors={errors}
              requireDrawnSignature={values.requireDrawnSignature}
              onRequireDrawnSignatureChange={(next) =>
                updateField("requireDrawnSignature", next as any)
              }
            />
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
              >
                <Button variant="outlined" onClick={handleCancelNav}>
                  Cancel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => handleOpenConfirm("preview")}
                  disabled={submitting || loadingStudy}
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={() => handleOpenConfirm("create")}
                  disabled={submitting || loadingStudy}
                >
                  Create
                </Button>
              </Stack>
            </Paper>
          </>
        ) : (
          <>
            <Paper sx={{ p: 2, border: 1, borderColor: "divider" }}>
              {loadingStudy && <LinearProgress sx={{ mb: 2 }} />}
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{ overflowX: "auto", pb: 1 }}
              >
                {STEP_LABELS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Box sx={{ mt: 3 }}>{renderStep()}</Box>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                sx={{ mt: 3 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                >
                  Previous
                </Button>
                {activeStep < STEP_LABELS.length - 1 ? (
                  <Button variant="contained" onClick={handleNext}>
                    Next
                  </Button>
                ) : (
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleCancelNav}>
                      Cancel
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => handleOpenConfirm("preview")}
                      disabled={submitting || loadingStudy}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircle />}
                      onClick={() => handleOpenConfirm("create")}
                      disabled={submitting || loadingStudy}
                    >
                      Create
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5, border: 1, borderColor: "divider" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    What this maps to in the consent form
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Seven consent blocks plus the consent and signature block.
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={1.5}>
                {sections.map((section) => (
                  <Grid item xs={12} md={6} key={section.key}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2, height: "100%" }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.5 }}
                      >
                        <Chip
                          label={`Block ${section.order}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          {section.title}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          fontSize: 12,
                          color: "text.secondary",
                          maxHeight: 96,
                          overflow: "auto",
                        }}
                        dangerouslySetInnerHTML={{ __html: section.bodyHtml }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </>
        )}
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => (submitting ? null : setConfirmOpen(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogAction === "preview"
            ? "Preview consent form"
            : dialogAction === "cancel"
              ? "Leave page?"
              : "Create consent form"}
        </DialogTitle>
        <DialogContent>
          {dialogAction === "create" ? (
            <DialogContentText sx={{ mb: 1.5 }}>
              You are about to create Version {nextVersion} of the consent form
              for <strong>{study?.name || "this study"}</strong>.
              {existingFormCount === 0
                ? " This will be set as the default active consent version automatically."
                : " The new version will be created without changing the current default. You can make it default from the management page."}
            </DialogContentText>
          ) : dialogAction === "preview" ? (
            <DialogContentText sx={{ mb: 1.5 }}>
              A preview of Version {nextVersion} for{" "}
              <strong>{study?.name || "this study"}</strong> will be generated
              and opened in a new tab.
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ mb: 1.5 }}>
              You are about to leave the consent form creator without creating a
              new version.
            </DialogContentText>
          )}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Consent version name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {values.formReferenceName || study?.name || "Untitled Study"} -
                Informed Consent
              </Typography>
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
            {dialogAction === "cancel" ? "Keep editing" : "Cancel"}
          </Button>
          {dialogAction === "preview" ? (
            <Button
              variant="contained"
              onClick={() => submitCreate("preview")}
              disabled={submitting}
              startIcon={<Visibility />}
            >
              {submitting ? "Generating..." : "Open preview"}
            </Button>
          ) : dialogAction === "cancel" ? (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setConfirmOpen(false);
                navigate(
                  `/researcher/study/${encodeURIComponent(studyId)}/consent-management`,
                );
              }}
              disabled={submitting}
            >
              Leave page
            </Button>
          ) : (
            <Tooltip title="Create and activate the new consent form">
              <span>
                <Button
                  variant="contained"
                  onClick={() => submitCreate("save")}
                  disabled={submitting}
                  startIcon={<CheckCircle />}
                >
                  {submitting ? "Creating..." : "Create form"}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
